import { useRequestBillingEmailVerificationAction } from '@/hooks/actions/use-request-billing-email-verification-action';
import { useVerifyBillingEmailAction } from '@/hooks/actions/use-verify-billing-email-action';
import { useResendCooldown } from '@/pages/signup/_components/use-resend-cooldown';
import { Button, PinInput, Stack, Text } from '@chakra-ui/react';
import { NoticePanel } from '@fluenceur/ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { billingEmailError } from './billing-email-error';
import { BillingTargetChangedError } from '@/hooks/actions/use-billing-action-scope';

export function BillingEmailStep({ refresh }: { refresh: () => Promise<unknown> }) {
  const { t } = useTranslation();
  const request = useRequestBillingEmailVerificationAction();
  const verify = useVerifyBillingEmailAction();
  const [sent, setSent] = useState<{ email: string; retryAfterSeconds: number } | null>(null);
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const lastCode = useRef<string | null>(null);
  const { remaining, restart } = useResendCooldown(sent?.retryAfterSeconds ?? 0);
  const send = async () => {
    if (request.isPending || (sent && remaining > 0)) return;
    setError(null);
    try {
      const result = await request.mutateAsync();
      setSent(result);
      setDigits(Array(6).fill(''));
      lastCode.current = null;
      restart();
    } catch (failure) {
      if (failure instanceof BillingTargetChangedError) return;
      setError(billingEmailError(failure, 'signup.verify.errors.sendFailed'));
      await refresh();
    }
  };
  const submit = async (code: string) => {
    if (!/^\d{6}$/.test(code) || submitting.current || lastCode.current === code) return;
    submitting.current = true;
    lastCode.current = code;
    setBusy(true);
    setError(null);
    try {
      await verify.mutateAsync({ code });
      await refresh();
    } catch (failure) {
      if (failure instanceof BillingTargetChangedError) return;
      setError(billingEmailError(failure, 'signup.verify.errors.invalid'));
      await refresh();
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  return (
    <Stack gap={4}>
      <NoticePanel tone="info" title={t('onboarding.billing.verifyEmailTitle')}>
        {t('onboarding.billing.verifyEmailBody')}
      </NoticePanel>
      {sent && (
        <>
          <Text>{t('signup.verify.sentTo', { email: sent.email })}</Text>
          <PinInput.Root
            value={digits}
            onValueChange={({ value }) => {
              setDigits(value);
              void submit(value.join(''));
            }}
            type="numeric"
            otp
            variant="outline"
            placeholder="0"
            disabled={busy}
          >
            <PinInput.Control>
              {Array.from({ length: 6 }, (_, index) => (
                <PinInput.Input key={index} index={index} />
              ))}
            </PinInput.Control>
          </PinInput.Root>
        </>
      )}
      {error && <NoticePanel tone="warning" title={t(error)} />}
      <Button
        variant="outline"
        onClick={() => void send()}
        loading={request.isPending}
        disabled={!!sent && remaining > 0}
      >
        {sent
          ? remaining > 0
            ? t('signup.verify.resendIn', { seconds: remaining })
            : t('signup.verify.resend')
          : t('onboarding.billing.sendEmailCode')}
      </Button>
    </Stack>
  );
}
